import authRepository from "@/api/auth.js";
import profileRepository from "@/api/profile.js";
import metaRepository from "@/api/meta.js";
import nodeRepository from "@/api/node.js";

const stateFactory = () => {
  return {
    status: {
      authenticated: localStorage.getItem("_AUTH_") || null,
      profileLoaded: false,
      metaLoaded: false
    },
    user: {
      roles: [],
      menu: [],
      navPermissions: [],
      nodePermissions: [],
      persistentNodesCache: {},
      dics: {},
      dicNames: [],
      lang: "ru"
    },
    meta: {
      classes: {},
      class_tags: {},
      class_children: {}
    },
    currentNodeCache: null,
    childrenNodesCache: {},
    parentNodesCache: {}
  };
};

export default {
  state: stateFactory(),

  actions: {
    async login({ commit, dispatch }, credentials) {
      try {
        await authRepository.login(credentials);
        commit("setAuth");
        await dispatch("fetchProfile");
        commit("clearNotices");
      } catch (error) {
        commit("removeAuth");
        return Promise.reject(error);
      }
    },

    async fetchProfile({ commit }) {
      const { data } = await profileRepository.getProfile();
      commit("setProfileData", data);
      commit("setProfileLoaded");
    },

    async fetchMeta({ commit }) {
      const { data } = await metaRepository.getMeta();
      commit("setMetaData", data);
      commit("setMetaLoaded");
    },

    async fetchCurrentNode({ commit, getters }, id) {
      const node =
        getters.getCurrentNode(id) || getters.getNodeFromAnyCache(id);
      if (!node) {
        const { data } = await nodeRepository.getCache(id);
        commit("setParentNodesCache", data.parentCache);
        const newNode = getters.getNodeFromAnyCache(id);
        if (newNode == null) {
          console.log(`repeat search for node ${id} -- not found!`);
        }
        commit("setCurrentNodeCache", newNode);
      } else {
        commit("setCurrentNodeCache", node);
      }
    },

    async fetchCurrentNodeForce({ commit, getters }, id) {
      const { data } = await nodeRepository.getCache(id);
      commit("setParentNodesCache", data.parentCache);
      const newNode = getters.getNodeFromParentCache(id);
      commit("setCurrentNodeCache", newNode);
    },

    async logout({ commit, getters }) {
      if (!getters.isAuth) return;
      await authRepository.logout();
      commit("removeAuth");
      commit("resetState");
    }
  },

  getters: {
    isAuth(state) {
      return state.status.authenticated;
    },
    isProfileLoaded(state) {
      return state.status.profileLoaded;
    },
    isMetaLoaded(state) {
      return state.status.metaLoaded;
    },
    getUserName(state) {
      return state.user.name;
    },
    getNavPermissions: state => {
      return state.user.navPermissions;
    },
    getRoles(state) {
      return state.user.roles;
    },
    getMyId(state) {
      return state.user.id;
    },
    getLang(state) {
      return state.user.lang;
    },
    getUserWorkplaceRoute(state) {
      const { workplace } = state.user;
      return { name: workplace || "Dashboard" };
    },
    hasNavPermission(state) {
      return namePermission =>
        state.user.navPermissions.indexOf(namePermission) >= 0;
    },
    getNodeFromParentCache(state) {
      return id => state.parentNodesCache[id] || null;
    },
    getNodeFromAnyCache(state) {
      return id => {
        return (
          state.user.persistentNodesCache[id] ||
          state.childrenNodesCache[id] ||
          state.parentNodesCache[id] ||
          null
        );
      };
    },
    getCurrentNode(state) {
      return id => {
        if ((state.currentNodeCache?.id || 0) != id) {
          return null;
        }
        return state.currentNodeCache;
      };
    },
    hasNodePermission(state, getters) {
      return (id, name) => {
        const node = getters.getNodeFromAnyCache(id);
        if (node === null) {
          return false;
        }
        const permissionCode = `Class${node.ac}/${name}`;
        return state.user.nodePermissions.indexOf(permissionCode) >= 0;
      };
    },
    isAllowedNav(state) {
      return workplace => {
        return state.user.menu.some(nav => nav.workplace === workplace);
      };
    },
    getNavMenu(state) {
      return state.user.menu;
    },
    getDicNames(state) {
      return state.user.dicNames;
    },
    getDic(state) {
      return (dicId, dicLang) => {
        if (state.user.dics[dicId][dicLang])
          return state.user.dics[dicId][dicLang];
        if (state.user.dics[dicId][""]) return state.user.dics[dicId][""];
        return [];
      };
    },
    getDicTranslation(state, getters) {
      return (dicId, dicLang, dicTerm) => {
        const dic = getters.getDic(dicId, dicLang);
        const entry = dic.find(entry => entry.term == dicTerm);
        return entry?.value || "";
      };
    },
    navTitle(state) {
      return moduleName => {
        const nav = state.user.menu.find(nav => nav.module_name === moduleName);
        if (!nav) return null;
        return nav.title ? nav.title : nav.module_name;
      };
    },
    getPrefixRoute(state) {
      return moduleName => {
        const nav = state.user.menu.find(nav => nav.module_name === moduleName);
        if (!nav) return undefined;
        return nav.prefix;
      };
    },
    getMeta(state) {
      return classId => ({
        class: state.meta.classes[classId] || null,
        tags: state.meta.class_tags[classId] || null,
        children: state.meta.class_children[classId] || [],
        references: state.meta.class_references[classId] || []
      });
    },
    getMetaReferences(state) {
      return classId => state.meta.class_references[classId];
    },
    getMetaLinkGroup(state) {
      return groupId => state.meta.class_reference_groups[groupId];
    },
    getMetaClass(state) {
      return classId => state.meta.classes[classId];
    },
    getMetaTags(state) {
      return classId => state.meta.class_tags[classId];
    },
    getMetaChildren(state) {
      return classId => state.meta.class_children[classId];
    }
  },

  mutations: {
    resetState(state) {
      Object.assign(state, stateFactory());
    },
    setAuth(state) {
      state.status.authenticated = true;
      localStorage.setItem("_AUTH_", true);
    },
    removeAuth(state) {
      state.status.authenticated = false;
      localStorage.removeItem("_AUTH_");
    },
    setProfileData(state, userData) {
      state.user = userData;
    },
    setCurrentNodeCache(state, nodeData) {
      state.currentNodeCache = nodeData;
    },
    setChildrenNodesCache(state, nodeData) {
      state.childrenNodesCache = nodeData;
    },
    setParentNodesCache(state, nodeData) {
      state.parentNodesCache = nodeData;
    },
    setLang(state, lang) {
      state.user.lang = lang;
    },
    setMetaData(state, metaData) {
      state.meta = metaData;
    },
    removeUserData(state) {
      state.user = {
        roles: [],
        menu: [],
        navPermissions: [],
        nodePermissions: [],
        dics: {},
        dicNames: [],
        persistentNodesCache: {}
      };
    },
    setProfileLoaded(state) {
      state.status.profileLoaded = true;
    },
    setMetaLoaded(state) {
      state.status.metaLoaded = true;
    }
  }
};
